// @ts-strict-ignore
import omit from 'lodash/omit';
import pick from 'lodash/pick';

import {ProductUpdate} from 'app_api/product_updates/types';
import {Section, SectionItem} from 'common/components/items-menu/types';
import {Permission, ReportName} from 'common/report/constants';
import {CurrentUser, Organization} from 'common/report/queries/app-api/user-metadata-client';
import {userDetailsStore} from 'common/report/stores/user-details';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {excludeDemos, getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {purgeStorage} from 'common/report/purge-storage';
import {StorageManager} from 'common/contexts/storage-manager/types';
import {HeaderTabs} from 'common/components/app-sidenav';
import {TEXT_HIGHLIGHT_THEME, TextHighlightEl} from 'common/components/text-highlight';
import {numberInUSD} from 'common/util';
import {usageAnalyticsClient} from 'common/report/queries/app-api/usage-analytics-client';
import {
  ChartDataEndpointPath,
  ChartDateRange,
  UsageAnalyticsContextTypes,
} from 'common/report/queries/app-api/usage-analytics-client-types';
import {fetchOrgProjectData} from 'common/report/queries/app-api/legacy/orgs-settings-client';

import {
  FULL_CHANGELOG_LINK,
  HelpSectionItemType,
  NavCategoryType,
  REPORT_INFO,
  SETTINGS_CATEGORY_TRACKING,
} from './constants';
import {HelpProductUpdateSectionItemValue, NavCategories, NavReports, ReportContext} from './types';

function getNavReports(reportContext: ReportContext): NavReports {
  return Object.keys(REPORT_INFO).reduce((obj, key) => {
    const report = REPORT_INFO[key];
    const result = {
      ...omit(report, `disabling`),
      name: key,
      endpoint: report.endpoint && report.endpoint(reportContext),
      hidden: report.hidden && report.hidden(reportContext),
      disabled: report.disabling?.isDisabled(reportContext) && report.disabling.disabledMsg,
    };
    obj[key] = result;
    return obj;
  }, {});
}

export function getNavReportsFromCategory(categoryType: NavCategoryType, reportContext: ReportContext): NavReports {
  const navReports = getNavReports(reportContext);
  return Object.keys(navReports)
    .filter((report) => navReports[report].category === categoryType && !navReports[report].hidden)
    .reduce((obj, key) => {
      obj[key] = navReports[key];
      return obj;
    }, {});
}

export function getNavCategories({reportContext}: {reportContext: ReportContext}): NavCategories {
  const getReports = function (categoryType) {
    return getNavReportsFromCategory(categoryType, reportContext);
  };

  const categories: NavCategories = {
    analysis: {
      label: NavCategoryType.Analysis,
      twoColumn: true,
      reports: getReports(NavCategoryType.Analysis),
    },

    users: {
      label: NavCategoryType.Users,
      reports: pick(getReports(NavCategoryType.Users), ReportName.Users),
    },

    dataManagement: {
      label: NavCategoryType.DataManagement,
      reports: getReports(NavCategoryType.DataManagement),
    },
  };

  // set name for each category and report based on its prop key and filter out hidden objects
  for (const [categoryName, category] of Object.entries(categories)) {
    category.name = categoryName;
    category.hidden && delete categories[categoryName];

    for (const [reportName, report] of Object.entries(category.reports)) {
      if (report.hidden) {
        delete category.reports[reportName];
      }
    }
  }

  return categories;
}

export function getProductUpdateSections(updates: ProductUpdate[]): Array<Section<HelpProductUpdateSectionItemValue>> {
  if (!updates.length) {
    return null;
  }

  return [
    {
      items: [
        ...updates.map((update) => ({
          label: update.label,
          value: {
            helpLink: update.link,
            reportName: update.report_name,
            type: HelpSectionItemType.ProductUpdate,
          } as HelpProductUpdateSectionItemValue,
        })),
        {
          label: ({t}) => t(`help.full-changelog`),
          value: {
            helpLink: FULL_CHANGELOG_LINK,
            type: HelpSectionItemType.ProductUpdate,
          },
        },
      ],
    },
  ];
}

export function getReportContext({currentProject}: {currentProject: CurrentProject}): ReportContext {
  const workspaceId = parseInt(getWorkspaceId(), 10) || null; // null instead of NaN
  let isProjectOrGlobalWorkspace: boolean;
  if (workspaceId) {
    const workspaces = Object.values(currentProject?.workspaces || {});
    const workspace = workspaces.find((currWorkspace) => currWorkspace.id === workspaceId);
    isProjectOrGlobalWorkspace = !workspace || workspace.isGlobal;
  } else {
    isProjectOrGlobalWorkspace = true;
  }
  return {
    projectId: parseInt(getProjectId(), 10),
    project: currentProject,
    isProjectOrGlobalWorkspace,
  };
}

export function getOrgSettingsMenuItem({
  currentUser,
  currentProject,
}: {
  currentUser?: CurrentUser;
  currentProject?: CurrentProject;
}): SectionItem | null {
  if (!currentProject) {
    return null;
  }

  // currentUser uses the raw `/me` endpoint and userDetailsStore uses the same endpoint but with `include_workspace_users=false`, which is a lot faster. Ideally we want to use `/me`, but we default to userDetailsStore to avoid having the UI not updated by a slow API call.
  const organizationsList = currentUser?.organizations || userDetailsStore.value?.organizations;
  const currentOrganization = organizationsList?.[currentProject.organizationId];
  const canAccessOrgSettings = currentOrganization?.permissions?.includes(Permission.ViewOrganizationSettings);

  if (currentOrganization) {
    return {
      ...currentOrganization,
      wrapLabel: true,
      label: ({t}) => t(`settings.organization`),
      href: getReportUrl(`settings/#org/${currentOrganization.id}`, {projectId: currentProject.id}),
      isDisabled: !canAccessOrgSettings,
      tooltip: {
        text: canAccessOrgSettings
          ? ({t}) => t(`settings.organization-name-settings`, {organization: currentOrganization.name})
          : ({t}) => t(`settings.organization-no-access`, {organization: currentOrganization.name}),
        size: DisplaySize.Medium,
        placement: Placement.Left,
      },
      tracking: SETTINGS_CATEGORY_TRACKING.Org,
    };
  }

  if (currentUser?.isStaff) {
    // staff should always receive org settings
    const orgId = currentProject.organizationId;
    return {
      wrapLabel: true,
      label: ({t}) => t(`settings.organization`),
      href: getReportUrl(`settings/#org/${orgId}`, {projectId: currentProject.id}),
      tooltip: {
        text: ({t}) => t(`settings.organization`),
        size: DisplaySize.Medium,
        placement: Placement.Left,
      },
      tracking: SETTINGS_CATEGORY_TRACKING.Org,
    };
  }

  return null;
}

export function getNavHeaderTabs({
  currentProject,
  currentUser,
}: {
  currentProject: CurrentProject | undefined;
  currentUser: CurrentUser | undefined;
}): HeaderTabs {
  const navSectionItems = [
    ...(currentProject?.id
      ? [
          getOrgSettingsMenuItem({
            currentUser,
            currentProject,
          }),
          getProjectSettingsMenuItem({currentProject}),
        ]
      : []),
    getAccountSettingsMenuItem({currentUser}),
  ].filter((item): item is SectionItem => Boolean(item));

  return navSectionItems.map((item) => {
    let label = null;

    if (item.href.includes('account')) {
      label = 'mixology.profile';
    } else if (item.href.includes('org')) {
      label = 'settings.organization-short';
    } else {
      label = 'mixology.project';
    }

    return {
      href: item.href,
      label: ({t}) => t(label),
      isDisabled: item.isDisabled,
      ...(item.tooltip && {
        tooltip: {...item.tooltip, placement: Placement.Bottom},
      }),
    };
  });
}

export function getProjectSettingsMenuItem({currentProject}: {currentProject?: CurrentProject}): SectionItem | null {
  if (!currentProject) {
    return null;
  }
  const canAccessProjectSettings = currentProject.hasPermission(Permission.AccessProjectSettings);
  const projectId = Number(currentProject.id);
  const projectName = currentProject.name;
  return {
    label: ({t}) => t(`settings.project`),
    wrapLabel: true,
    href: getReportUrl(`settings/#project/${projectId}`, {
      projectId,
    }),
    isDisabled: !canAccessProjectSettings,
    tooltip: {
      text: canAccessProjectSettings
        ? ({t}) => t(`settings.project-tooltip`, {projectName})
        : ({t}) => t(`settings.project-no-access`, {projectName}),
      size: DisplaySize.Medium,
      placement: Placement.Left,
    },
    value: projectName,
    tracking: SETTINGS_CATEGORY_TRACKING.Project,
  };
}

export function getAccountSettingsMenuItem({currentUser}: {currentUser?: CurrentUser}): SectionItem {
  return {
    label: ({t}) => t(`settings.personal`),
    href: `/settings/account`,
    value: `Profile & Preferences`,
    tracking: SETTINGS_CATEGORY_TRACKING.Account,
    id: currentUser?.id,
  };
}

export function getStartupCreditPromoSectionItem({
  currentProject,
}: {
  currentProject?: CurrentProject;
}): SectionItem | null {
  if (!currentProject?.hasStartupCreditAndFreeUser()) {
    return null;
  }

  const startupCreditBalances = currentProject.getStartupCreditBalances()!;
  return {
    label: `Upgrade to Growth Plan`,
    // TODO: Tried adding VNode to `subLabel` type, but causes type errors elsewhere. Not sure why...
    // @ts-ignore TS2322: Type VNode is not assignable to type Translatable
    subLabel: TextHighlightEl({
      attrs: {
        theme: TEXT_HIGHLIGHT_THEME.INFORMATION,
        text: `${numberInUSD(startupCreditBalances.creditUsageAmount, {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        })} of ${numberInUSD(startupCreditBalances.initialValue / 1000, {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        })}k credits used`,
        length: `wide`,
      },
    }),
    hasCaret: true,
    persistCaret: true,
    caretStyle: `arrow`,
    href: `/pricing/plan-builder`,
    highlightItem: currentProject?.hasStartupCreditAndFreeUser(),
  };
}

export function getFilteredOrgs(
  organizations: Record<string, Organization>,
  {permissions, excludeDemoOrgs = false}: {permissions?: Permission[]; excludeDemoOrgs?: boolean},
) {
  let orgList = Object.values(organizations);
  if (permissions) {
    orgList = orgList.filter((o) => permissions.every((p) => o.permissions && o.permissions.includes(p)));
  }
  if (excludeDemoOrgs) {
    orgList = excludeDemos(orgList);
  }
  return orgList;
}

export function logout({localStorageManager}: {localStorageManager: StorageManager}): void {
  purgeStorage(localStorageManager);
  mixpanel.reset();
  mixpanel.idmgmt?.reset();
  window.location.href = `/logout`;
}

/**
 * Warm up the slowest and most often used usage analytics charts.
 */
export function warmUsageAnalyticsData({
  currentUser,
  currentProject,
}: {
  currentUser?: CurrentUser;
  currentProject?: CurrentProject;
}): void {
  if (currentProject?.hasPermission(Permission.ViewUsageAnalytics)) {
    usageAnalyticsClient.getChartData({
      usageContext: UsageAnalyticsContextTypes.Project,
      id: currentProject.id,
      chartEndpoint: ChartDataEndpointPath.WeeklyActiveUsers,
      chartDateRange: ChartDateRange.Weeks12,
    });

    usageAnalyticsClient.getChartData({
      usageContext: UsageAnalyticsContextTypes.Project,
      id: currentProject.id,
      chartEndpoint: ChartDataEndpointPath.MostActiveUsers,
      chartDateRange: ChartDateRange.Weeks12,
    });
  }

  const organizationId = currentProject?.organizationId;
  if (currentUser?.organizations[organizationId]?.permissions.includes(Permission.ViewUsageAnalytics)) {
    usageAnalyticsClient.getChartData({
      usageContext: UsageAnalyticsContextTypes.Organization,
      id: organizationId,
      chartEndpoint: ChartDataEndpointPath.AverageWeeklyActiveUsersByProject,
      chartDateRange: ChartDateRange.Weeks12,
    });

    usageAnalyticsClient.getChartData({
      usageContext: UsageAnalyticsContextTypes.Organization,
      id: organizationId,
      chartEndpoint: ChartDataEndpointPath.MostActiveUsersByProject,
      chartDateRange: ChartDateRange.Weeks12,
    });
  }

  if (currentUser?.organizations[organizationId]?.permissions.includes(Permission.ViewOrgProjects)) {
    fetchOrgProjectData(organizationId, {includeProjectUsageData: true});
  }
}
